/**
 * Partner request form submission hook.
 *
 * Encapsulates form submission logic for creating and updating partner requests.
 * Handles API calls, error handling, and success notifications. Manages
 * submission state to prevent duplicate submissions.
 *
 * @module usePartnerRequestForm
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { PartnerRequest, GermanLevel } from '@/types/partner-finder';
import { createPartnerRequest, updatePartnerRequest } from '@/lib/partnerApi';
import { logger } from '@/lib/logger';

interface FormData {
  level: GermanLevel;
  meetingPreference: 'online' | 'in-person';
  goal: string;
  availability: string[];
  location?: string;
  timezone?: string;
  additionalInfo: string;
}

interface UsePartnerRequestFormReturn {
  readonly isSubmitting: boolean;
  readonly handleSubmit: (
    values: FormData,
    userId: string,
    initialData?: PartnerRequest,
  ) => Promise<boolean>;
}

/**
 * Hook for handling partner request form submission.
 *
 * Encapsulates form submission logic for creating and updating partner requests.
 * Handles API calls, error handling, success notifications, and submission state
 * management to prevent duplicate submissions.
 *
 * @returns Object containing:
 *   - `isSubmitting`: Boolean indicating if form submission is in progress
 *   - `handleSubmit`: Function to submit the form with form values, user ID, and optional initial data
 *
 * @example
 * ```typescript
 * const { isSubmitting, handleSubmit } = usePartnerRequestForm();
 *
 * const onSubmit = async (values: FormData) => {
 *   const success = await handleSubmit(values, userId, initialData);
 *   if (success) {
 *     navigate('/partners');
 *   }
 * };
 * ```
 */
export function usePartnerRequestForm(): UsePartnerRequestFormReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (
    values: FormData,
    userId: string,
    initialData?: PartnerRequest,
  ): Promise<boolean> => {
    if (!userId) {
      toast.error('You must be logged in to create a request');
      return false;
    }

    setIsSubmitting(true);
    try {
      // Map form values to API format
      const apiData = {
        userId,
        level: values.level,
        isOnline: values.meetingPreference === 'online',
        goal: values.goal,
        suggestedDays: values.availability,
        location: values.location,
        moreInfo: values.additionalInfo,
      };

      if (initialData) {
        // Update existing request
        const updated = await updatePartnerRequest(initialData.id, apiData);
        if (updated) {
          toast.success('Request updated successfully');
          return true;
        }
      } else {
        // Create new request
        const created = await createPartnerRequest(apiData);
        if (created) {
          toast.success('Request created successfully');
          return true;
        }
      }
      return false;
    } catch (error) {
      logger.error('Error submitting request', error);
      toast.error('Failed to save request');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, handleSubmit };
}

